import { DomainError } from '../errors/index.ts';

/**
 * ADR-061. Office (human) authorisation.
 *
 * DELIBERATELY NOT the ADR-027 capability system next door in ../capability/.
 * That answers "has this ORGANISATION bought / is it legally allowed this
 * feature" (plan, add-on, country). This answers "may THIS PERSON do this
 * inside an organisation that already has the feature". Merging them would
 * make it impossible to express the ordinary case where an org has
 * ADVANCED_BILLING and a PICKER still must not touch a tariff - and both
 * checks genuinely run, in that order, at the API boundary.
 *
 * The matrix below is the enforced boundary, not documentation of one. There
 * is no second copy in a doc or a database table that could drift from it, and
 * tests/integration/api/office-roles.test.ts asserts the whole matrix
 * endpoint-by-endpoint against a real database.
 */

export const PERMISSIONS = [
  // warehouse floor
  'RECEIVING.WRITE',
  // The decision on a receiving surplus (accept / reject / handled): a commercial, stock-affecting decision, so ADMIN only
  // (the MATRIX gives ADMIN every permission and no other role this one). Distinct from RECEIVING.WRITE, which the floor has.
  'RECEIVING.RESOLVE_OVERAGE',
  'PUTAWAY.WRITE',
  'PICKING.WRITE',
  'PACKING.WRITE',
  'SHIPPING.WRITE',
  'RETURNS.WRITE',
  'INVENTORY.READ',
  'INVENTORY.WRITE',
  // reference data a scan flow cannot function without (sku, location, barcode)
  'REFERENCE.READ',
  'ORDERS.READ',
  /**
   * Creating a manual (operator-entered) sales order from Ops. Reserves real
   * stock on create, so it is narrower than ORDERS.READ and never implied by
   * any floor-execution permission: ADMIN (implicitly) and WAREHOUSE only.
   */
  'ORDERS.WRITE',
  // commercial
  'BILLING.READ',
  'BILLING.WRITE',
  /** Read-only proof behind a charge: the movement/task/operation it came from. */
  'EVIDENCE.READ',
  /** Seller-facing visibility for support and dispute handling. */
  'SELLER.READ',
  // migration
  'IMPORT.READ',
  'IMPORT.WRITE',
  // office administration
  'USERS.READ',
  'USERS.WRITE',
  // marketplace channel connections (ADR-067)
  'CHANNELS.READ',
  'CHANNELS.WRITE',
  /** Narrower than CHANNELS.WRITE on purpose - writing a channel CONNECTION (owner/kind/warehouse) never implies the right to write its SECRET. Marketplace Shared Foundation epic. */
  'CHANNELS.CREDENTIALS.WRITE',
  // FBS EXECUTION epic - the label pipeline (request/retry/print). Narrower
  // than PACKING.WRITE on purpose: printing is a distinct, retry-able,
  // physically-consequential action (wastes a real sticker on a real
  // thermal printer), not implied by the general packing permission.
  'LABELS.WRITE',
  // REGULATED EVIDENCE epic — capturing a photo is a distinct, physically-
  // consequential write (same reasoning as LABELS.WRITE next door), narrower
  // than RECEIVING.WRITE/RETURNS.WRITE/PACKING.WRITE so a role that can
  // READ evidence (BILLING/SUPPORT, for disputes) never implicitly gets to
  // CREATE it.
  'EVIDENCE.WRITE',
  // tenant onboarding state machine (ADR-076 Part A) - deliberately ADMIN-only
  // (see MATRIX below): the steps it reads span every other domain (legal
  // entity, warehouse, billing, users), so no narrower role can safely see
  // the whole picture, and activation is a one-way, organisation-wide switch.
  'ONBOARDING.READ',
  'ONBOARDING.WRITE',
  // Minimal org-structure CRUD (ADR-076 Part A) - legal entity/warehouse/
  // system locations/seller creation. Did not exist anywhere before this
  // epic (previously only reachable via direct SQL or the import engine);
  // ADMIN-only, same reasoning as ONBOARDING.* above.
  'ORG.WRITE',
  // ADR-077 - the TENANT-facing half of the SaaS subscription layer. READ is
  // granted to ADMIN and BILLING (the same pair that already sees invoices
  // and tariffs); WRITE is ADMIN-only and is deliberately NARROW - it covers
  // requesting a plan change, cancelling at period end and revoking that
  // cancellation, and NOTHING that grants the organisation a paid feature.
  // Granting features is the platform boundary's job (apps/api/src/routes/
  // platform.ts), which an office token cannot reach at all: different
  // prefix, different middleware, different identity table.
  'SUBSCRIPTION.READ',
  'SUBSCRIPTION.WRITE',
  // COMMERCIAL COMPLETENESS epic (ADR-078) Part D - tenant data export spans
  // every domain in the organisation (same reasoning as ONBOARDING.*/
  // ORG.WRITE above), ADMIN-only.
  'EXPORT.READ',
  'EXPORT.WRITE',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export const OFFICE_ROLES = ['ADMIN', 'WAREHOUSE', 'PICKER', 'PACKER', 'BILLING', 'SUPPORT'] as const;
export type OfficeRole = (typeof OFFICE_ROLES)[number];

export function isOfficeRole(value: string): value is OfficeRole {
  return (OFFICE_ROLES as readonly string[]).includes(value);
}

/**
 * The matrix. Read it as the epic states it:
 *
 *   ADMIN      full office-level access within the organisation
 *   WAREHOUSE  receiving / putaway / inventory / returns
 *   PICKER     picking + the reference data a pick screen needs
 *   PACKER     packing / shipping preparation + that same reference data
 *   BILLING    tariffs / charges / invoices + read access to operations evidence
 *   SUPPORT    seller visibility and disputes, NO financial mutation
 *
 * Nothing is granted "for convenience". In particular SUPPORT has BILLING.READ
 * but never BILLING.WRITE (it must be able to explain a disputed charge and
 * must not be able to change one), and BILLING has no floor-operation write at
 * all (it may prove what happened, never restate it).
 *
 * Impersonation is explicitly NOT in this epic; `audit_log.impersonated_by`
 * exists from ADR-050 and stays unused.
 */
const MATRIX: Readonly<Record<OfficeRole, readonly Permission[]>> = {
  ADMIN: PERMISSIONS,

  WAREHOUSE: [
    'RECEIVING.WRITE',
    'PUTAWAY.WRITE',
    'RETURNS.WRITE',
    'INVENTORY.READ',
    'INVENTORY.WRITE',
    'REFERENCE.READ',
    'ORDERS.READ',
    'ORDERS.WRITE',
    'EVIDENCE.READ',
    'EVIDENCE.WRITE',
  ],

  PICKER: ['PICKING.WRITE', 'INVENTORY.READ', 'REFERENCE.READ', 'ORDERS.READ'],

  PACKER: [
    'PACKING.WRITE',
    'SHIPPING.WRITE',
    'LABELS.WRITE',
    'INVENTORY.READ',
    'REFERENCE.READ',
    'ORDERS.READ',
    'EVIDENCE.WRITE',
  ],

  BILLING: [
    'BILLING.READ',
    'BILLING.WRITE',
    'EVIDENCE.READ',
    'SELLER.READ',
    'REFERENCE.READ',
    'ORDERS.READ',
    // ADR-077: sees the subscription; can change nothing about it.
    'SUBSCRIPTION.READ',
  ],

  SUPPORT: ['SELLER.READ', 'EVIDENCE.READ', 'BILLING.READ', 'REFERENCE.READ', 'ORDERS.READ', 'INVENTORY.READ'],
};

function buildSets(): Record<OfficeRole, ReadonlySet<Permission>> {
  const out = {} as Record<OfficeRole, ReadonlySet<Permission>>;
  for (const role of OFFICE_ROLES) out[role] = new Set(MATRIX[role]);
  return out;
}
const SETS: Readonly<Record<OfficeRole, ReadonlySet<Permission>>> = buildSets();

export function permissionsForRole(role: OfficeRole): readonly Permission[] {
  return MATRIX[role];
}

export function roleHasPermission(role: OfficeRole, permission: Permission): boolean {
  return SETS[role].has(permission);
}

/**
 * The guard every office route calls. Throws COMMON.FORBIDDEN, which
 * apps/api/src/errors.ts already maps to HTTP 403 - no new status mapping and
 * no rendered English string (ADR-020).
 */
export function requirePermission(role: OfficeRole, permission: Permission): void {
  if (!SETS[role].has(permission)) {
    throw new DomainError('COMMON.FORBIDDEN', { role, permission });
  }
}
